<template>
  <div id="enrolmentForm">
    <div class="container">
      <div class="datatable-edit__header">
        <h3>Application List</h3>
      </div>

      <el-input v-model="searchValue" class="search-datatable mb-3" placeholder="Search" id="inputSearchClassName">
        <template slot="prepend">
          <i class="material-icons">search</i>
          <span>Search</span>
        </template>
      </el-input>

      <!-- Loading state -->
      <div v-if="listCount === 0" class="emptylist-info">
        <div class="emptylist__img">
          <img src="../../assets/table-loading.png" />
        </div>
      </div>

      <!-- Data tables -->
      <div v-else-if="listCount > 0" class="datatable_group">
        <el-tabs type="card" @tab-click="handleClick">
          <el-tab-pane v-for="tab in tabs" :key="tab.key" :label="tab.label">
            <div v-if="tab.data.length > 0 || ['Pending', 'Reviewing', 'Draft'].includes(tab.key)">
              <div :class="`${tab.key.toLowerCase()}List`">
                <h4>{{ tab.label }}</h4>
              </div>
              <data-tables :filters="filters" :data="tab.data" :action-col="actionCol">
                <el-table-column v-for="column in columns" :key="column.prop" :prop="column.prop" :label="column.label" sortable="custom" />
              </data-tables>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- Empty state -->
      <div v-else class="emptylist-info">
        <div class="emptylist__img">
          <img src="../../assets/table-loading.png" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataSource from '../../data/datasource'
import { lang } from '../../locale'
import Cookies from 'js-cookie'
import { getFormatDate } from '@/helper/utily'

const columns = [
  { prop: 'APF_CreatedOnFormatted', label: 'Created Date' },
  { prop: 'APF_FirstName', label: 'First Name' },
  { prop: 'APF_Surname', label: 'Last Name' },
  { prop: 'DetailedStatus', label: 'Enrolment Status' },
  { prop: 'CommencementDate', label: 'Commencement Date' },
  { prop: 'APF_IDType', label: 'ID Type' },
  { prop: 'AcademicYear', label: 'Academic Year' },
  { prop: 'AcademicLevel', label: 'Academic Level' },
]

export default {
  name: 'ApplicationList',
  data() {
    return {
      listCount: 0,
      applications: [],
      searchValue: '',
    }
  },
  computed: {
    columns() {
      return columns
    },
    actionCol() {
      return {
        label: lang('More'),
        props: { align: 'center' },
        buttons: [
          {
            props: { type: 'primary', icon: 'el-hand-up' },
            handler: row => this.$router.push(`/ApplicationManagement?id=${row.APF_ID}`),
            label: lang('View'),
          },
        ],
      }
    },
    pendingApplications() {
      return this.applications.filter(app => app.DetailedStatus === 'Pending')
    },
    reviewingApplications() {
      return this.applications.filter(app => app.DetailedStatus === 'Reviewing')
    },
    approvedApplications() {
      return this.applications.filter(app => app.APF_Status === 'Approved')
    },
    rejectedApplications() {
      return this.applications.filter(app => app.APF_Status === 'Rejected')
    },
    closedApplications() {
      return this.applications.filter(app => app.APF_Status === 'Closed')
    },
    draftApplications() {
      return this.applications.filter(app => app.APF_Status === 'Draft')
    },
    tabs() {
      return [
        { key: 'Pending', label: 'Pending', data: this.pendingApplications },
        { key: 'Reviewing', label: 'Reviewing', data: this.reviewingApplications },
        { key: 'Approved', label: 'Approved', data: this.approvedApplications },
        { key: 'Rejected', label: 'Rejected', data: this.rejectedApplications },
        { key: 'Closed', label: 'Closed', data: this.closedApplications },
        { key: 'Draft', label: 'Draft', data: this.draftApplications },
      ]
    },
    filters() {
      return [
        {
          value: this.searchValue,
          prop: ['APF_FirstName', 'APF_Surname'],
        },
      ]
    },
  },
  async mounted() {
    window.VUE = this

    // Check if we have cached data from previous visits
    if (window.applicationListCache?.length > 0) {
      this.applications = window.applicationListCache
      this.listCount = window.applicationListCache.length
    } else {
      // Only fetch data if we don't have cached data
      await this.getApplicationList()
    }
  },
  methods: {
    lang,
    async getApplicationList() {
      this.$vs.loading()

      try {
        const resp = await DataSource.shared.getApplicationList(Cookies.get('schoolSession'))

        if (resp.code === '2') {
          this.$notify.error({
            title: 'Data not found',
            message: 'Data not found!',
          })
        } else {
          this.listCount = resp.length

          this.applications = resp.map(app => ({
            ...app,
            CommencementDate: app.APF_CommencementDate?.slice(0, 10),
            DetailedStatus: app.APF_Status === 'Pending' && app.ApprovedCount > 0 ? 'Reviewing' : app.APF_Status,
            APF_CreatedOnFormatted: getFormatDate(app.APF_CreatedOn, 'YYYY-MM-DD'),
          }))

          // Cache the data globally for future visits
          window.applicationListCache = this.applications
        }
      } catch (error) {
        console.error('Error fetching applications:', error)
      } finally {
        this.$vs.loading.close()
      }
    },
    handleClick(tab, event) {
      console.log(tab, event)
    },
  },
}
</script>

<style scoped>
.pendingList {
  background-color: rgb(144, 179, 243);
  width: 100%;
  border-radius: 5px;
  color: white;
}

.reviewingList {
  background-color: rgb(144, 179, 243);
  width: 100%;
  border-radius: 5px;
  color: white;
}

.approvedList {
  background-color: rgb(17, 100, 0);
  width: 100%;
  border-radius: 5px;
  color: white;
}

.rejectedList {
  background-color: red;
  width: 100%;
  border-radius: 5px;
  color: white;
}

.closedList {
  background-color: gray;
  width: 100%;
  border-radius: 5px;
  color: white;
}

.draftList {
  background-color: rgb(144, 179, 243);
  width: 100%;
  border-radius: 5px;
  color: white;
}

.el-table td {
  padding: 1px 0;
}
</style>
